/**
 * Auction Hub Banner — Countdown timer, live show info, photo slideshow,
 * auction-specific badges and info lightbox.
 *
 * Reads config from window.MHV_AUCTION_HUB_BANNER_CONFIG
 * Fetches live status + event detail from schedule API.
 */
(function() {
  'use strict';

  var bannerConfig = window.MHV_AUCTION_HUB_BANNER_CONFIG || {};
  var liveLink = bannerConfig.liveLink || '/pages/live';
  var sessionCode = bannerConfig.sessionCode || '';
  var activeCountdownInterval = null; // tracked so a second start() can replace the first

  // =========================================================================
  // Auto-fit the auction-title headline
  // =========================================================================
  // Keeps the headline at its full (large) CSS size and lets a long title wrap
  // to extra lines rather than shrinking — so it stays bigger than the countdown
  // label. Only shrinks in the edge case where the content still overflows its
  // width when wrapped (a single unbreakable word wider than the container),
  // which would otherwise force a horizontal scrollbar. Runs on load, when the
  // API swaps in the real title, on resize, and after web fonts settle.
  function fitHeadline() {
    var el = document.getElementById('hub-auction-title');
    if (!el || !el.textContent.trim()) return;
    // Reset to the full CSS size and allow normal wrapping.
    el.style.fontSize = '';
    el.style.whiteSpace = '';
    if (el.clientWidth <= 0) return; // hidden/collapsed — skip
    var fs = parseFloat(getComputedStyle(el).fontSize) || 24;
    var guard = 0;
    // Step down only while a wrapped line still exceeds the container width.
    while (el.scrollWidth > el.clientWidth + 1 && fs > 16 && guard < 24) {
      fs -= 2;
      el.style.fontSize = fs + 'px';
      guard++;
    }
  }
  var _fitRaf = null;
  function scheduleFit() {
    if (_fitRaf) cancelAnimationFrame(_fitRaf);
    _fitRaf = requestAnimationFrame(fitHeadline);
  }
  window.addEventListener('resize', scheduleFit);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleFit);
  // Let the collapse/expand script re-fit when the banner expands from a
  // collapsed (zero-width) state on load.
  window.mhvFitHubHeadline = scheduleFit;

  document.addEventListener('DOMContentLoaded', function() {
    // Start the countdown immediately from the injected config so the digits
    // stop spinning right away — no waiting on the network round-trip. The
    // schedule API call below refines this (bidding phase, ended state, event
    // detail) once it returns.
    if (bannerConfig.livetime) {
      startCountdownFromAPI({ livetime: bannerConfig.livetime, ended: bannerConfig.auctionEnded });
    }

    // Fit the Liquid fallback title now; refits when the API title swaps in.
    scheduleFit();

    // Fetch live status from schedule API (includes event detail)
    var apiUrl = MHV.SERVER_URL + '/api/schedule/live-status';
    if (sessionCode) apiUrl += '?session=' + encodeURIComponent(sessionCode);

    // Shared with the bid Quick Facts lightbox (snippets/mhv-absentee-info-lightbox), which
    // explains a TIMED sale from the same answer instead of asking the server again.
    var liveStatusPromise = fetch(apiUrl).then(function(r) { return r.json(); });
    window.__mhvLiveStatusPromise = liveStatusPromise;
    liveStatusPromise
      .then(function(data) {
        startCountdownFromAPI(data);
        // Event title lives at the top level of the response (data.title), not
        // inside data.event. Override the Liquid fallback once it loads.
        if (data.title) {
          var titleEl = document.getElementById('hub-auction-title');
          if (titleEl) titleEl.textContent = data.title;
          scheduleFit();
        }
        if (data.event) {
          renderAuctionBadges(data.event);
          populateInfoLightbox(data);
          applyAuthoritativeBuyersPremium(data.event);
        }
      })
      .catch(function(err) {
        console.error('Auction hub banner: failed to fetch live status', err);
        if (bannerConfig.auctionEnded) {
          var h3 = document.querySelector("#countdown h3");
          if (h3) h3.innerText = "The Auction Has Ended! Thank you!";
        }
      });

    // ⚠️ NO SLIDESHOW HERE (removed 2026-10-18). It cycled the same slides as the
    // rotator in sections/auction-hub-banner.liquid, on its own timer with no load gate
    // and a list captured once — two rotators on one slideshow, and appended lot photos
    // (data-gallery-lots) it could never reach. The section's rotator is the only one.
  });

  // =========================================================================
  // Countdown timer
  // =========================================================================
  function startCountdownFromAPI(data) {
    /* A POSTPONED sale has no date (the scheduler took it, and the livetime metafield
       with it). Say so — a countdown to nothing reads as a broken page, and one left
       over from the cancelled date is worse. */
    if (data && data.postponed) {
      if (activeCountdownInterval) { clearInterval(activeCountdownInterval); activeCountdownInterval = null; }
      var ph3 = document.querySelector("#countdown h3");
      if (ph3) ph3.innerText = "POSTPONED — NEW DATE TO BE ANNOUNCED";
      var ptimer = document.getElementById("timer");
      if (ptimer) ptimer.style.display = "none";
      var pjoin = document.getElementById("join-button-container");
      if (pjoin) pjoin.style.display = "none";
      var plabel = document.getElementById('live-auction-on-label');
      if (plabel) plabel.style.display = "none";
      return;
    }
    var liveTimeStr = data.livetime || bannerConfig.livetime || '';
    var auctionEnded = data.ended || bannerConfig.auctionEnded || false;
    var biddingOpenTime = data.bidding_open_time || null;
    var h3El = document.querySelector("#countdown h3");

    if (!liveTimeStr || liveTimeStr.length < 16) {
      if (auctionEnded) {
        if (h3El) h3El.innerText = "AUCTION ENDED";
        var timer = document.getElementById("timer");
        if (timer) timer.style.display = "none";
        var joinBtn = document.getElementById("join-button-container");
        if (joinBtn) joinBtn.style.display = "none";
      }
      return;
    }

    var year   = parseInt(liveTimeStr.slice(0, 4), 10);
    var month  = parseInt(liveTimeStr.slice(5, 7), 10);
    var day    = parseInt(liveTimeStr.slice(8, 10), 10);
    var hour   = parseInt(liveTimeStr.slice(11, 13), 10);
    var minute = parseInt(liveTimeStr.slice(14, 16), 10);

    var countdownDate = MHV.easternToUTC(year, month, day, hour, minute);
    var biddingDate = biddingOpenTime ? new Date(biddingOpenTime) : null;
    var liveOnLabel = document.getElementById('live-auction-on-label');

    // ── WHEN ONLINE BIDDING STOPS ─────────────────────────────
    // The deadline the server computes (lib/bidding-close.js — hour_before / at_start
    // / custom), served as `absentee_close_time`. It is the fact bidders kept missing:
    // the feedback was that people did not realise online bidding ended before the sale
    // did, or that there was no live online bidding at all. So the hero counts to the
    // CLOSE first and only then to the sale.
    //
    // ⚠️ `when_sold` sends no close time, and an `at_start` close lands on the sale
    // itself — neither is a phase of its own, and a phase that lasts one tick would
    // flash a label nobody can read. One minute of daylight is the test.
    /* ⏱️ AN ONLINE TIMED CLOSE SALE (2026-10-25) has no livestream, no auctioneer and no
       room: its start time IS the moment lots start closing, one at a time. So the hero
       counts to "LOTS START CLOSING", shows the time in Eastern WITH the zone, and once
       it passes says the lots are closing — never "IN-PERSON AUCTION". There is no
       separate online close for it (each lot takes bids until its own turn). */
    var isTimed = !!(data.event && data.event.auction_kind === 'online-timed-close');
    var closeDate = (!isTimed && data.absentee_close_time) ? new Date(data.absentee_close_time) : null;
    if (closeDate && isNaN(closeDate.getTime())) closeDate = null;
    var closeIsPhase = !!(closeDate && closeDate.getTime() < (countdownDate.getTime() - 60000));
    var CLOSES_LABEL = 'ONLINE BIDDING CLOSES IN';

    // Livestream vs in-person drives the countdown wording + the go-live CTA. Prefer the
    // refined API event; fall back to the Liquid-injected flag for the immediate paint.
    var isLivestream = (data.event && typeof data.event.livestream !== 'undefined')
      ? !!data.event.livestream : !!bannerConfig.livestream;
    var startsLabel = isTimed ? 'LOTS START CLOSING IN'
      : isLivestream ? 'LIVESTREAM STARTS IN' : 'IN-PERSON AUCTION STARTS IN';
    var liveOnLabelEl = document.getElementById('live-auction-on-label');
    if (isTimed && liveOnLabelEl) liveOnLabelEl.textContent = 'Lots start closing on';
    // Once online bidding has closed, what is left IS the live auction — say so. A sale
    // with no close deadline never passes through that window and keeps its own wording.
    var liveLabel = startsLabel;

    // Determine phase: 'bidding' → 'closing' → 'live' → 'done'
    var phase = 'live';
    if (biddingDate && new Date() < biddingDate) {
      phase = 'bidding';
    } else if (closeIsPhase && Date.now() < closeDate.getTime()) {
      phase = 'closing';
    }
    // After the close the clock counts to the START of the sale and says so (owner,
    // 2026-10-30: "count down to when bidding closes … then when bidding has closed,
    // count down to when the auction starts"). It used to read "LIVE AUCTION IN";
    // it now keeps the same "… STARTS IN" words a sale with no close deadline uses.

    function setCountdownDigit(id, value) {
      var el = document.getElementById(id);
      if (!el) return;
      el.classList.remove('spinning');
      el.innerText = value;
    }

    function updateCountdownDisplay(distance) {
      var days    = Math.floor(distance / (1000 * 60 * 60 * 24));
      var hours   = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      var seconds = Math.floor((distance % (1000 * 60)) / 1000);

      days    = days < 10 ? "0" + days : days;
      hours   = hours < 10 ? "0" + hours : hours;
      minutes = minutes < 10 ? "0" + minutes : minutes;
      seconds = seconds < 10 ? "0" + seconds : seconds;

      setCountdownDigit("days", days);
      setCountdownDigit("hours", hours);
      setCountdownDigit("minutes", minutes);
      setCountdownDigit("seconds", seconds);
    }

    // Replace any countdown started by a prior call (e.g. the immediate
    // config-based start before the API refined the data) so we never run two.
    if (activeCountdownInterval) clearInterval(activeCountdownInterval);

    var timerInterval = setInterval(function() {
      var now = new Date().getTime();

      if (phase === 'bidding') {
        var biddingDist = biddingDate.getTime() - now;
        if (biddingDist <= 0) {
          // Bidding just opened — count to whichever deadline comes next.
          phase = (closeIsPhase && now < closeDate.getTime()) ? 'closing' : 'live';
          if (h3El) h3El.innerText = (phase === 'closing') ? CLOSES_LABEL : liveLabel;
          if (liveOnLabel) liveOnLabel.style.visibility = (phase === 'closing') ? 'visible' : 'hidden';
        } else {
          if (h3El) h3El.innerText = "BIDDING OPENS IN";
          if (liveOnLabel) liveOnLabel.style.visibility = 'visible';
          updateCountdownDisplay(biddingDist);
          return;
        }
      }

      if (phase === 'closing') {
        var closeDist = closeDate.getTime() - now;
        if (closeDist > 0) {
          if (h3El && h3El.innerText !== CLOSES_LABEL) h3El.innerText = CLOSES_LABEL;
          // Keep "IN-PERSON AUCTION ON <date>" underneath: while the clock is counting
          // the CLOSE, the sale's own date is the thing the label no longer says.
          if (liveOnLabel) liveOnLabel.style.visibility = 'visible';
          updateCountdownDisplay(closeDist);
          return;
        }
        // Online bidding just closed — hand over to the countdown to the sale itself.
        phase = 'live';
        if (h3El) h3El.innerText = liveLabel;
        if (liveOnLabel) liveOnLabel.style.visibility = 'hidden';
      }

      var distance = countdownDate.getTime() - now;

      if (distance < 0) {
        clearInterval(timerInterval);
        document.getElementById("timer").style.display = "none";

        if (auctionEnded) {
          if (h3El) h3El.innerText = "AUCTION ENDED";
          document.getElementById("join-button-container").style.display = "none";
        } else if (isTimed) {
          // The bidding is right here — the NOW CLOSING bar at the foot of this page.
          if (h3El) h3El.innerHTML = '<span class="blinking-dot"></span> LOTS CLOSING NOW';
          document.getElementById("join-button-container").style.display = "none";
        } else if (isLivestream) {
          if (h3El) h3El.innerHTML = '<a href="' + liveLink + '" style="text-decoration:none; color:inherit;"><span class="blinking-dot"></span> LIVE NOW</a>';
          document.getElementById("live-show-info").style.display = "none";
          document.getElementById("join-button-container").innerHTML = '<a href="' + liveLink + '" class="button" style="margin-bottom: 10px">JOIN & BID LIVE</a>';
          document.getElementById("join-button-container").style.display = "block";
        } else {
          // In-person, no livestream: the sale is underway on the floor — there's no
          // stream to "join," so show status instead of a JOIN & BID LIVE button.
          if (h3El) h3El.innerHTML = '<span class="blinking-dot"></span> IN-PERSON AUCTION IN PROGRESS';
          document.getElementById("join-button-container").style.display = "none";
        }
        phase = 'done';
        return;
      }

      if (h3El && h3El.innerText !== liveLabel) h3El.innerText = liveLabel;
      updateCountdownDisplay(distance);
    }, 1000);
    activeCountdownInterval = timerInterval;

    // Paint the first frame immediately so the digits stop spinning at once,
    // instead of waiting up to 1s for the first interval tick.
    if (phase === 'bidding') {
      if (h3El) h3El.innerText = "BIDDING OPENS IN";
      if (liveOnLabel) liveOnLabel.style.visibility = 'visible';
      var initDist = biddingDate.getTime() - new Date().getTime();
      if (initDist > 0) updateCountdownDisplay(initDist);
    } else if (phase === 'closing') {
      if (h3El) h3El.innerText = CLOSES_LABEL;
      if (liveOnLabel) liveOnLabel.style.visibility = 'visible';
      var initCloseDist = closeDate.getTime() - new Date().getTime();
      if (initCloseDist > 0) updateCountdownDisplay(initCloseDist);
    } else {
      var initLiveDist = countdownDate.getTime() - new Date().getTime();
      if (initLiveDist > 0) { if (h3El) h3El.innerText = liveLabel; updateCountdownDisplay(initLiveDist); }
    }

    // Display the show's date and time.
    /* ⚠️ AN IN-PERSON SALE IS SHOWN IN THE ROOM'S TIME, WITH NO ZONE (owner, 2026-10-13:
       "for in person auctions lets hide EDT / EST"). This line used to print the
       VISITOR'S local time with its zone — right for an online sale anybody can join
       from anywhere, and wrong for one you have to drive to: a bidder in Ohio was told
       an hour that is not when the doors open. So in person it is Eastern wall clock
       (the building's) and the zone label is dropped; online keeps local time + zone. */
    var inPersonSale = !!(data.event && String(data.event.format || '').indexOf('in-person') === 0);
    // A timed sale: Eastern wall clock WITH its zone (EST/EDT for the date) — the same
    // words the server's description prints, so the page and the facts agree.
    var tzOpt = (inPersonSale || isTimed) ? { timeZone: 'America/New_York' } : {};
    function part(o) { return countdownDate.toLocaleString('en-US', Object.assign({}, tzOpt, o)); }
    var dayOfWeek = part({ weekday: 'long' });
    var monthName = part({ month: 'long' });
    var dayNum = Number(part({ day: 'numeric' }));
    var fullYear = Number(part({ year: 'numeric' }));
    var monthNum = Number(part({ month: 'numeric' }));
    var ordinal = (dayNum === 1 || dayNum === 21 || dayNum === 31) ? 'st'
      : (dayNum === 2 || dayNum === 22) ? 'nd'
      : (dayNum === 3 || dayNum === 23) ? 'rd' : 'th';
    var localHours = Number(part({ hour: 'numeric', hour12: false })) % 24;
    var localMinutes = Number(part({ minute: 'numeric' }));
    var ampm = localHours >= 12 ? 'pm' : 'am';
    localHours = localHours % 12;
    if (localHours === 0) { localHours = 12; }
    var timeStr = (localMinutes === 0)
      ? localHours + ampm
      : localHours + ":" + (localMinutes < 10 ? "0" + localMinutes : localMinutes) + ampm;
    var tzString = inPersonSale ? ''
      : isTimed ? countdownDate.toLocaleTimeString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' }).split(' ').pop()
      : countdownDate.toLocaleTimeString(undefined, { timeZoneName: 'short' }).split(' ').pop();

    // Short format for the banner (e.g. "THURSDAY 3/19/26 8PM EDT")
    var shortDay = dayOfWeek.toUpperCase();
    var shortMonth = monthNum;
    var shortYear = String(fullYear).slice(2);
    var shortTime = timeStr.toUpperCase();
    // A dot between the day and the time, so the line reads as two facts (2026-10-30).
    var bannerShowInfo = (shortDay + ' ' + shortMonth + '/' + dayNum + '/' + shortYear + ' · ' + shortTime + ' ' + tzString).trim();
    var infoEl = document.getElementById("live-show-info");
    if (infoEl) infoEl.innerText = bannerShowInfo;

    // Long format for lightbox (e.g. "Thursday, March 19th, 2026" + "8pm EDT")
    window._mhvLiveShowDate = dayOfWeek + ', ' + monthName + ' ' + dayNum + ordinal + ', ' + fullYear;
    window._mhvLiveShowTime = (timeStr + ' ' + tzString).trim();
  }

  // Human list of the default pickup locations for an event.
  function pickupText(ev) {
    var p = ev.pickup_defaults || {};
    var locs = [];
    if (p.franklin) locs.push('Franklin');
    if (p.jaffrey) locs.push('Jaffrey');
    if (p.onsite) locs.push('on site');
    if (!locs.length && ev.location && ev.format !== 'online-only') locs.push(ev.location === 'jaffrey' ? 'Jaffrey' : 'Franklin');
    if (locs.length >= 3) return locs.slice(0, -1).join(', ') + ', or ' + locs[locs.length - 1];
    if (locs.length === 2) return locs[0] + ' or ' + locs[1];
    return locs[0] || '';
  }

  // Preview-window formatting → { day: 'Fri Aug 14', time: '2–6 PM' } for the panel.
  function fmtHour(t) {
    var p = String(t).split(':'); var h = parseInt(p[0], 10); var m = p[1] || '00';
    if (isNaN(h)) return null;
    var ap = h >= 12 ? 'PM' : 'AM'; var hh = h % 12; if (hh === 0) hh = 12;
    return { ap: ap, label: hh + (m !== '00' ? (':' + m) : '') };
  }
  function formatPreviewDay(d) {
    if (!d || !d.date) return null;
    var parts = String(d.date).split('-');
    var dt = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    if (isNaN(dt.getTime())) return null;
    var day = dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
    var s = d.start ? fmtHour(d.start) : null, e = d.end ? fmtHour(d.end) : null;
    var time = '';
    // Compress the meridiem when both ends share it: "2–6 PM" not "2 PM–6 PM".
    if (s && e) time = (s.ap === e.ap) ? (s.label + '–' + e.label + ' ' + e.ap) : (s.label + ' ' + s.ap + '–' + e.label + ' ' + e.ap);
    else if (s) time = s.label + ' ' + s.ap;
    return { day: day, time: time };
  }

  /* What each kind of auction IS — one sentence for the badge tooltip and the Auction
     Info modal both (owner, 2026-10-13: the Discovery tooltip was "not accurate"). It
     restates the Bidder Terms' own descriptions (§ Auction formats) and may say less,
     never otherwise. ⚠️ An in-person sale WITH online absentee bidding is still an
     in-person sale: the old tooltip only knew "in-person-only", so every such Discovery
     sale read "lots revealed one at a time", which is how they are NOT sold. */
  function auctionTypeExplain(ev) {
    var key = (ev.auction_type || '').replace('online-', '').replace('in-person-', '');
    var online = ev.format === 'online-only';
    if (ev.auction_kind === 'online-timed-close') {
      return 'Every lot is photographed and described in the catalog, then closes online one at a time on a one-minute clock. No livestream and no auctioneer.';
    }
    if (ev.auction_type === 'claim-sale') return 'Fixed Buy-It-Now prices — no bidding. The first person to claim an item wins it at the listed price. No buyer’s premium.';
    if (key === 'antique' || key === 'catalogue') {
      return online
        ? 'Every lot is photographed, described and sold one at a time, livestreamed, with live online bidding.'
        : 'Every lot is photographed and described in a catalog, then sold one at a time in the room with paddle bidding.';
    }
    if (key === 'discovery') {
      return online
        ? 'A fast-paced online sale — lots are revealed live. You never know what’s next!'
        : 'A fast-paced sale in the room — box lots, groupings, furniture and single items. The room is set out in sections, and each section sells on choice: the high bidder takes as many lots as they want at their winning bid, then bidding starts again for what’s left.';
    }
    if (key === 'benefit') return 'A charity auction — proceeds support a good cause.';
    return '';
  }

  /** livetime '2026/10/18-14:00' (Eastern wall clock) → 'Sunday, October 18, 2026 at
      2:00 PM' — the same words registration step 1 prints for the same sale. */
  function fullWhen(livetime) {
    var m = String(livetime || '').match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})(?:-(\d{1,2}):(\d{2}))?/);
    if (!m) return '';
    var d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    var out = days[d.getUTCDay()] + ', ' + months[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
    if (m[4] != null) {
      var h = +m[4];
      out += ' at ' + ((h % 12) || 12) + ':' + m[5] + ' ' + (h >= 12 ? 'PM' : 'AM');
    }
    return out;
  }

  /** 'Upper Hall' / 'Lower Hall' for a Franklin sale, else null. */
  function hallLabel(ev) {
    var halls = { 'upper-hall': 'Upper Hall', 'lower-hall': 'Lower Hall' };
    return (ev && ev.location === 'franklin' && halls[ev.location_detail]) || null;
  }

  // =========================================================================
  // Preview times — a corner tab on the photo header (owner, 2026-10-13)
  // =========================================================================
  /* Lower right of the header, the mirror of the RSVP tab at the lower left and built
     the same way: a small tab that opens UPWARD into a panel with a × to put it away.
     It floats OVER the header, so opening it pushes nothing on the page down.
     ⚠️ OPEN ON A DESKTOP, CLOSED ON A PHONE (owner, 2026-10-13: "on desktop, lets have
     preview times expanded like we do the rsvp"). A desktop header has room for both
     panels at once, one in each corner; a phone's does not, where the RSVP panel alone
     already covers half of it. Minimising is remembered like the RSVP's
     (`mhv_pv_min`), so somebody who put it away is not shown it again. */
  function renderHeroPreview(ev) {
    var host = document.getElementById('hub-pv');
    if (!host) return;
    var pv = (ev.preview_days || []).map(formatPreviewDay).filter(Boolean);
    if (!pv.length) { host.hidden = true; host.innerHTML = ''; return; }
    var rows = pv.map(function(o) {
      return '<div class="hub-pv-row"><span class="hub-pv-day">' + MHV.escapeHtml(o.day) + '</span>' +
        (o.time ? '<span class="hub-pv-time">' + MHV.escapeHtml(o.time) + '</span>' : '') + '</div>';
    }).join('');
    host.innerHTML =
      '<div class="hub-pv-panel" id="hub-pv-panel" role="dialog" aria-label="Preview times" hidden>' +
        '<button type="button" class="hub-pv-x" aria-label="Close preview times">&times;</button>' +
        '<div class="hub-pv-head">Preview times</div>' +
        '<div class="hub-pv-sub">See the lots in person before the sale.</div>' +
        '<div class="hub-pv-rows">' + rows + '</div>' +
      '</div>' +
      '<button type="button" class="hub-pv-tab" aria-expanded="false" aria-controls="hub-pv-panel">Preview Times</button>';
    host.hidden = false;
    var panel = host.querySelector('.hub-pv-panel');
    var tab = host.querySelector('.hub-pv-tab');
    function setOpen(on) {
      panel.hidden = !on;
      host.classList.toggle('open', on);
      tab.setAttribute('aria-expanded', on ? 'true' : 'false');
    }
    function remember(min) { try { localStorage.setItem('mhv_pv_min', min ? '1' : ''); } catch (e) { /* private mode */ } }
    tab.addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); setOpen(true); remember(false); });
    host.querySelector('.hub-pv-x').addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); setOpen(false); remember(true); });
    var minimised = false;
    try { minimised = localStorage.getItem('mhv_pv_min') === '1'; } catch (e) { /* private mode */ }
    if (window.matchMedia && window.matchMedia('(min-width: 750px)').matches && !minimised) setOpen(true);
    // The header is a link to the gallery — a tap inside the panel must not follow it.
    panel.addEventListener('click', function(e) { e.stopPropagation(); });
  }

  // =========================================================================
  // Auction badges (inline on banner, below description)
  // =========================================================================
  function renderAuctionBadges(ev) {
    var container = document.getElementById('hub-auction-badges');
    if (!container) return;

    /* ⚠️ A STATIC SALE GETS NO BADGE ROW (owner, 2026-10-03) — and it was not
       merely unwanted, it was WRONG. Every badge here is auction vocabulary, and
       on the first static sale the row rendered "Online / This auction takes
       place entirely online. Participate from anywhere…" above a fixed-price
       shop. The format, auction type, livestream, absentee and premium badges
       describe things a shop does not have.
       ⚠️ Guarded HERE rather than only in CSS because this function writes the
       container's `display` inline, and an inline style beats a stylesheet rule.
       The CSS `!important` in auction-hub-banner.liquid is the belt to this. */
    if (bannerConfig.staticSale) {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }

    var _online = ev.format === 'online-only';
    var _inPerson = ev.format === 'in-person-only';
    var _hybrid = ev.format === 'in-person-and-online';
    var _isClaimSale = ev.auction_type === 'claim-sale';
    var _aucKey = (ev.auction_type || '').replace('online-', '').replace('in-person-', '');

    var badges = [];

    // Format — the AUCTION itself is in person or online; online participation for an
    // in-person sale is via Absentee Bidding (its own badge), so both in-person formats
    // read simply "In-Person".
    var fmtLabels = { 'online-only': 'Online', 'in-person-and-online': 'In-Person', 'in-person-only': 'In-Person' };
    var fmtTips = {
      'online-only': 'This auction takes place entirely online. Participate from anywhere with an internet connection.',
      'in-person-and-online': 'This auction is held in person. You can also bid online ahead of time via absentee bidding.',
      'in-person-only': 'This auction is in-person only. Join us at the venue to participate.'
    };
    if (ev.format && fmtLabels[ev.format]) {
      badges.push({ cls: 'format', label: fmtLabels[ev.format], tip: fmtTips[ev.format] || '' });
    }
    // The hall, straight after In-Person (owner, 2026-10-13) — where in the building.
    var hall = !_online ? hallLabel(ev) : null;
    if (hall) {
      badges.push({ cls: 'hall', label: hall, tip: 'This sale is held in the ' + hall + (ev.address ? ' at ' + ev.address : '') + '.' });
    }

    // Auction type — "Catalog Auction" (curated, catalogued) vs "Discovery Auction".
    var aucLabels = { 'antique': 'Catalog Auction', 'discovery': 'Discovery Auction', 'benefit': 'Benefit Auction', 'claim-sale': 'Claim Sale',
      'online-catalogue': 'Catalog Auction', 'online-discovery': 'Discovery Auction', 'in-person-antique': 'Catalog Auction', 'in-person-discovery': 'Discovery Auction' };
    if (ev.auction_type && aucLabels[ev.auction_type]) {
      var aucTip = auctionTypeExplain(ev);
      badges.push({ cls: 'auction', label: aucLabels[ev.auction_type], tip: aucTip });
    }

    // Livestream
    if (!_isClaimSale) {
      if (ev.livestream) {
        badges.push({ cls: 'livestream', label: 'Livestream', tip: _online
          ? 'This auction is livestreamed on the Live Page. Watch and bid in real time!'
          : 'This auction is livestreamed on the Live Page. The bidding happens in the room.' });
      }
    }

    // Absentee — a badge again (owner, 2026-10-13, after an afternoon as a three-column
    // box), with the detail in its tooltip: How / When / style, from lib/bidding-facts
    // on the server (ev.bidding_facts). Rose, the colour the box wore.
    var bf = ev.bidding_facts;
    if (!_isClaimSale && bf && bf.timed && window.MHVBiddingFacts) {
      // ⏱️ A timed sale: ONE way to bid all along — never called "absentee".
      badges.push({ cls: 'absentee', label: 'Timed Closing', tipHtml: window.MHVBiddingFacts.tooltip(bf), wide: true });
    } else if (!_isClaimSale && bf && bf.absentee && window.MHVBiddingFacts) {
      badges.push({ cls: 'absentee', label: 'Absentee Bidding', tipHtml: window.MHVBiddingFacts.tooltip(bf), wide: true });
    }

    // Buyer's premium
    if (!_isClaimSale) {
      var prem = ev.buyers_premium != null ? ev.buyers_premium : 15;
      // The 2% follows WHERE you pay (lib/zelle-discount): Zelle on an emailed invoice,
      // cash or check at the front desk — never "Zelle, cash, or check" in one breath.
      var absOnline = !!(ev.bidding_facts && ev.bidding_facts.online);
      var premTip = prem === 0 ? 'No buyer\'s premium on this auction.'
        : 'A ' + prem + '% buyer\'s premium applies to all lots. ' + (
          _online ? 'Pay your invoice by Zelle'
          : absOnline ? 'Pay by cash or check at the front desk, or by Zelle on an emailed invoice,'
          : 'Pay by cash or check at the front desk') + ' to save 2% (' + (prem - 2) + '%).';
      badges.push({ cls: 'premium', label: prem + '% Buyer\'s Premium', tip: premTip });
    }

    // Shipping availability (per-auction default; buyers care whether it ships)
    if (!_isClaimSale) {
      // ⚠️ PREFER shipping_mode_effective — the mode counted off the LOTS by the server.
      // `shipping_mode` is only the default set when the sale was created, so a sale
      // made "pickup only" whose lots were later ticked shippable badged wrongly.
      var sm = ev.shipping_mode_effective || ev.shipping_mode || (_online ? 'all' : 'none');
      var pu = pickupText(ev);
      var puClause = pu ? (' Pick up in ' + pu + '.') : '';
      if (sm === 'all') badges.push({ cls: 'shipping', label: 'Ships Nationwide', tip: 'Every lot can be shipped anywhere in the U.S.' + (pu ? (' Local pickup in ' + pu + ' is also available.') : '') });
      else if (sm === 'none') badges.push({ cls: 'shipping', label: 'Local Pickup Only', tip: 'This auction is local pickup only — no shipping.' + puClause });
      else if (sm === 'some') badges.push({ cls: 'shipping', label: 'Some Lots Ship', tip: 'Some lots ship and some are local pickup only — check each lot.' + puClause });
    }

    // RESERVES (owner, 2026-10-26) — when any lot of the sale has a reserve. The server
    // counts them off the catalogue (ev.has_reserves — a boolean, never an amount); the
    // tooltip is the reserve rule in plain words (mhv-reserve.js RULE, the same words as
    // lib/bidding-facts and the Bidder Terms). Hidden on a blind sale (no reserve badge
    // is published there) and a claim sale (nothing is bid).
    if (!_isClaimSale && ev.has_reserves && !ev.absentee_blind) {
      var resTip = (window.MHVReserve && window.MHVReserve.RULE) ||
        'Some lots have a reserve, a confidential minimum the bidding must reach before the lot can sell. A lot that never reaches its reserve passes (doesn’t sell).';
      badges.push({ cls: 'reserves', label: 'Reserves', tip: resTip, wide: true });
    }

    // Location — NOT a badge (owner, 2026-10-13): the address sits ABOVE this row and
    // the hall is its own badge beside In-Person.

    if (badges.length === 0) return;

    /* ── THE ADDRESS, ABOVE THE BADGES (owner, 2026-10-13). The hall is a badge in the
       row now, beside In-Person; the preview times are a tab on the photo header. */
    var html = '';
    if (!_online) {
      var where = '';
      if (ev.address) {
        where = '<a class="hub-chip" href="https://maps.google.com/?q=' + encodeURIComponent(ev.address) + '" target="_blank" rel="noopener">📍 ' + MHV.escapeHtml(ev.address) + '</a>';
      } else if (ev.location) {
        where = '<span class="hub-chip">📍 ' + (ev.location === 'jaffrey' ? 'Jaffrey, NH' : 'Franklin, NH') + '</span>';
      }
      if (where) html += '<div class="hub-where">' + where + '</div>';
    }
    html += '<div class="schedule-badges hub-badges">';
    for (var i = 0; i < badges.length; i++) {
      var b = badges[i];
      if (b.tip || b.tipHtml) {
        html += '<span class="hub-badge-wrap">';
        html += '<span class="schedule-badge schedule-badge--' + b.cls + ' hub-badge-hover" tabindex="0">' + MHV.escapeHtml(b.label) + '</span>';
        // tipHtml is built by MHVBiddingFacts.tooltip, which escapes every value it prints.
        html += '<span class="hub-badge-tooltip' + (b.wide ? ' hub-badge-tooltip--wide' : '') + '">' + (b.tipHtml || MHV.escapeHtml(b.tip)) + '</span>';
        html += '</span>';
      } else {
        html += '<span class="schedule-badge schedule-badge--' + b.cls + '">' + MHV.escapeHtml(b.label) + '</span>';
      }
    }
    html += '</div>';
    container.innerHTML = html;
    renderHeroPreview(ev);
    /* ⚠️ A BANNER RESTORED COLLAPSED HIDES THIS ROW (html.hub-banner-preclosed →
       display:none !important), and a hidden element measures 0 tall. Capping it at
       "0 + 40px" left a 40px window that nothing ever widened — no transition runs on a
       hidden element, so the transitionend that lifts the cap never fired — and
       expanding the banner on a phone showed the badges and a sliver of the box beneath
       them (owner, 2026-10-13: "it only goes down just til a little after the row of
       badges"). Collapsed ⇒ no animation and no display change: leave it hidden and
       UNCAPPED, and expand() shows it whole. */
    var heroEl = document.querySelector('.collection-hero');
    var bannerCollapsed = document.documentElement.classList.contains('hub-banner-preclosed') ||
      !!(heroEl && heroEl.classList.contains('collection-hero--collapsed'));
    if (bannerCollapsed) {
      container.style.padding = '0.75rem 1rem';
      container.style.maxHeight = 'none';
      container.style.overflow = 'visible';
    } else {
      container.style.display = 'block';
      // Animate fold-down
      requestAnimationFrame(function() {
        container.style.padding = '0.75rem 1rem';
        container.style.maxHeight = container.scrollHeight + 40 + 'px';
      });
    }
    // After the fold-down completes, switch to overflow:visible so badge tooltips
    // (which extend above on desktop / below on mobile) can render outside the
    // container. Inline overflow:hidden is needed during the max-height transition,
    // but clips the tooltips once expanded.
    container.addEventListener('transitionend', function onExpandEnd(e) {
      if (e.propertyName !== 'max-height') return;
      container.style.overflow = 'visible';
      // The height measured at reveal is not the height after a rotation — let it grow.
      container.style.maxHeight = 'none';
      container.removeEventListener('transitionend', onExpandEnd);
    });

    /* A tooltip is centred on its badge, so one near the edge of a phone ran off it — and
       the absentee tooltip is wide. Shift it back on screen and move its arrow by the
       same amount so it still points at the badge. */
    function clampTip(wrap) {
      var tip = wrap.querySelector('.hub-badge-tooltip');
      if (!tip) return;
      tip.style.setProperty('--tip-shift', '0px');
      var r = tip.getBoundingClientRect();
      if (!r.width) return;
      var vw = document.documentElement.clientWidth, pad = 8, shift = 0;
      if (r.left < pad) shift = pad - r.left;
      else if (r.right > vw - pad) shift = (vw - pad) - r.right;
      tip.style.setProperty('--tip-shift', shift + 'px');
    }
    container.querySelectorAll('.hub-badge-wrap').forEach(function(wrap) {
      wrap.addEventListener('mouseenter', function() { clampTip(wrap); });
      wrap.addEventListener('focusin', function() { clampTip(wrap); });
    });

    // Tap-to-toggle tooltips on mobile only (desktop uses CSS :hover)
    if (window.innerWidth < 750) {
      container.querySelectorAll('.hub-badge-wrap').forEach(function(wrap) {
        wrap.addEventListener('click', function(e) {
          e.stopPropagation();
          var isActive = wrap.classList.contains('hub-badge-wrap--active');
          // Close all others
          container.querySelectorAll('.hub-badge-wrap--active').forEach(function(w) {
            w.classList.remove('hub-badge-wrap--active');
          });
          if (!isActive) { wrap.classList.add('hub-badge-wrap--active'); clampTip(wrap); }
        });
      });
      // Close tooltip when tapping elsewhere
      document.addEventListener('click', function() {
        container.querySelectorAll('.hub-badge-wrap--active').forEach(function(w) {
          w.classList.remove('hub-badge-wrap--active');
        });
      });
    }
  }

  // =========================================================================
  // Buyer's premium — ONE source of truth: the event row in our own database
  // =========================================================================
  // HOUSE RULE (owner, 2026-08-15 — Q1/Q2): the event + our DB are authoritative; Shopify
  // is an output, not a source. The long arc of this project is moving off Shopify, so
  // nothing customer-facing should depend on a Shopify metafield for a number we own.
  //
  // What this fixes: the bid-confirmation lightbox read
  // `collection.metafields.custom.buyers_premium | default: 10` while the banner beside it
  // read `ev.buyers_premium` (defaulting to 15 for an in-person sale). An in-person auction
  // whose collection metafield was unset therefore ADVERTISED 15% in the banner and QUOTED
  // 10% in the bid box — on the same page, to the same bidder.
  //
  // The Liquid value stays only as the pre-API paint. This overwrites it the moment the
  // authoritative number arrives, and re-renders any open summary so a bidder who opened
  // the box early doesn't keep looking at the stale figure.
  function applyAuthoritativeBuyersPremium(ev) {
    if (!ev) return;
    // Respect a real 0. `!= null` (not a truthiness test) — 0% is a genuine arrangement,
    // and reading it as "unset" is exactly the bug this is replacing.
    var prem = (ev.buyers_premium != null && ev.buyers_premium !== '')
      ? Number(ev.buyers_premium)
      : 15;   // one premium, every format (2026-09-18)
    if (!isFinite(prem)) return;

    var inputs = document.querySelectorAll('input[id^="bp_pct_"]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (Number(el.value) === prem) continue;
      el.value = String(prem);

      // Keep the "Buyer's Premium (N%)" label honest too — it is baked into Liquid.
      var pid = el.id.slice('bp_pct_'.length);
      var summary = document.getElementById('bid_summary_' + pid);
      if (summary) {
        var spans = summary.getElementsByTagName('span');
        for (var s = 0; s < spans.length; s++) {
          if (/^Buyer's Premium \(/.test(spans[s].textContent || '')) {
            spans[s].textContent = "Buyer's Premium (" + prem + '%)';
            break;
          }
        }
      }
      // If this lot's box is already open with an amount typed, recompute it.
      var bidEl = document.getElementById('max_bid_' + pid + '_lightbox');
      if (bidEl && bidEl.value && typeof window.updateBidSummary === 'function') {
        try { window.updateBidSummary(pid, parseFloat(bidEl.value) || 0); } catch (e) { /* non-fatal */ }
      }
    }
  }

  // =========================================================================
  // Auction Info modal (2026-10-13 rebuild)
  // =========================================================================
  /* ⚠️ IT SAYS WHAT THE SALE SAYS — rebuilt 2026-10-13, when a year of drift was found in
     it: "Zelle, cash, or check" on an online sale (cash and check are not taken online —
     Zelle follows WHERE you pay), "all items are available for nationwide shipping" on
     every online sale whatever its lots said, "watch and bid via the chat" (bidding is the
     bid bar), and an absentee line that knew nothing of closing times or blind sales.
     The bidding half is now lib/bidding-facts on the server — the SAME object the hub's
     box draws — and the rest reads the event row the badges read. Its look is the
     registration modal's (white card, 16px base, em sizes), and its badges wear the hub
     row's colours, one per kind of fact (mhv-schedule.css, the hub palette). */
  function populateInfoLightbox(data) {
    var ev = data.event;
    if (!ev) return;
    var contentEl = document.getElementById('hub-info-content');
    if (!contentEl) return;

    var esc = MHV.escapeHtml;
    var _online = ev.format === 'online-only';
    var _isClaimSale = ev.auction_type === 'claim-sale';
    var _aucKey = (ev.auction_type || '').replace('online-', '').replace('in-person-', '');
    var facts = ev.bidding_facts || null;
    var onlineAbsentee = !!(facts && facts.online);

    var html = '';
    var heading = bannerConfig.staticSale ? 'About this sale' : 'About this auction';
    html += '<h2 class="hub-info-title" id="hub-info-title">' + heading + '</h2>';

    /* When and where — the registration step's layout (owner, 2026-10-13): the full date
       and time; the address with the hall in plain words on the same line (no badge);
       then what the bidding style means, in the NEUTRAL note; then the loud line. */
    var when = fullWhen(data.livetime) || [window._mhvLiveShowDate, window._mhvLiveShowTime].filter(Boolean).join(' at ');
    var timedFacts = facts && facts.timed ? facts.timed : null;
    if (timedFacts && when) when = 'Lots start closing ' + when + (timedFacts.zone ? ' ' + timedFacts.zone : '');
    var hall = hallLabel(ev);
    var whereTxt = !_online ? (ev.address || (ev.location === 'jaffrey' ? 'Jaffrey, NH' : ev.location ? 'Franklin, NH' : '')) : '';
    if (when || whereTxt) {
      html += '<div class="hub-info-when">';
      if (when) html += '<div class="hub-info-when__date">' + esc(when) + '</div>';
      if (whereTxt) {
        html += '<div class="hub-info-when__where">' +
          (ev.address ? '<a href="https://maps.google.com/?q=' + encodeURIComponent(ev.address) + '" target="_blank" rel="noopener">' + esc(whereTxt) + '</a>' : esc(whereTxt)) +
          (hall ? ' · ' + esc(hall) : '') + '</div>';
      }
      html += '</div>';
    }

    if (facts && window.MHVBiddingFacts) {
      html += window.MHVBiddingFacts.styleNote(facts);
      html += window.MHVBiddingFacts.notice(facts);
    }

    var items = [];

    // Format
    if (ev.format === 'online-only') {
      items.push({ cls: 'format', label: 'Online', explain: 'This auction happens entirely online — bid from anywhere.' });
    } else if (ev.format) {
      items.push({ cls: 'format', label: 'In-Person', explain: 'This auction happens in the room — come to the sale to bid with a paddle.' });
    }

    // Auction type
    if (ev.auction_type) {
      var aucLabels = { 'antique': 'Catalog Auction', 'discovery': 'Discovery Auction', 'benefit': 'Benefit Auction', 'claim-sale': 'Claim Sale',
        'online-catalogue': 'Catalog Auction', 'online-discovery': 'Discovery Auction', 'in-person-antique': 'Catalog Auction', 'in-person-discovery': 'Discovery Auction' };
      var aucExplain = auctionTypeExplain(ev);
      items.push({ cls: 'auction', label: aucLabels[ev.auction_type] || ev.auction_type, explain: aucExplain });
    }

    // Livestream
    if (!_isClaimSale) {
      if (ev.livestream) {
        items.push({ cls: 'livestream', label: 'Livestream', explain: _online
          ? 'Watch on the <a href="/pages/live">Live Page</a> and bid in real time.'
          : 'Watch on the <a href="/pages/live">Live Page</a>. The bidding itself happens in the room.' });
      } else {
        items.push({ cls: 'nolivestreamdetail', label: 'No Livestream', explain: timedFacts
          ? 'No video stream and no auctioneer — the lots close one at a time right here on the website.'
          : _online
          ? 'No video stream — bid right here on the website during the sale.'
          : 'No video stream — this auction happens in the room.' });
      }
    }

    // Buyer's premium — the 2% follows WHERE you pay (lib/zelle-discount):
    // Zelle on an emailed invoice, cash or check at the desk.
    if (!_isClaimSale) {
      var prem = ev.buyers_premium != null ? Number(ev.buyers_premium) : 15;
      var premExplain;
      if (!prem) {
        premExplain = 'No buyer’s premium on this auction.';
      } else {
        var save = 'to save 2% (' + (prem - 2) + '% instead of ' + prem + '%).';
        premExplain = 'A ' + prem + '% buyer’s premium is added to each winning bid. ' + (
          _online ? 'Pay your invoice by Zelle ' + save
          : onlineAbsentee ? 'Pay by cash or check at the front desk — or by Zelle when you pay an emailed invoice — ' + save
          : 'Pay by cash or check at the front desk ' + save);
      }
      items.push({ cls: 'premium', label: (prem || 0) + '% Buyer’s Premium', explain: premExplain });
    }

    // Shipping / pickup — counted off the LOTS (shipping_mode_effective)
    if (!_isClaimSale) {
      var smL = ev.shipping_mode_effective || ev.shipping_mode || (_online ? 'all' : 'none');
      var puL = pickupText(ev);
      if (smL === 'all') items.push({ cls: 'shipping', label: 'Ships Nationwide', explain: 'Every lot can be shipped anywhere in the U.S.' + (puL ? (' Local pickup in ' + puL + ' is also available.') : '') });
      else if (smL === 'some') items.push({ cls: 'shipping', label: 'Some Lots Ship', explain: 'Some lots ship and some are local pickup only — check each lot.' + (puL ? (' Pick up in ' + puL + '.') : '') });
      else items.push({ cls: 'shipping', label: 'Local Pickup Only', explain: 'Local pickup only — no shipping.' + (puL ? (' Pick up in ' + puL + '.') : '') });
    }

    // Absentee — the same How / When / style the hub badge's tooltip shows
    if (timedFacts && window.MHVBiddingFacts) {
      items.push({ cls: 'absentee', label: 'Timed Closing', explain: window.MHVBiddingFacts.tooltip(facts) });
    } else if (facts && facts.absentee && window.MHVBiddingFacts) {
      items.push({ cls: 'absentee', label: 'Absentee Bidding', explain: window.MHVBiddingFacts.tooltip(facts) });
    } else if (facts && !_isClaimSale) {
      items.push({ cls: 'nolivestreamdetail', label: 'No Absentee Bidding', explain: 'There is no absentee bidding for this sale — come to the sale to bid.' });
    }

    // Preview
    if (ev.preview_days && ev.preview_days.length && !_online) {
      var pvL = ev.preview_days.map(formatPreviewDay).filter(Boolean);
      if (pvL.length) {
        items.push({ cls: 'preview', label: 'Preview', explain: 'See the lots in person before the sale:<br>' +
          pvL.map(function(o) { return esc(o.day + (o.time ? (' · ' + o.time) : '')); }).join('<br>') });
      }
    }

    if (items.length) {
      html += '<div class="hub-info-rows">';
      items.forEach(function(it) {
        html += '<div class="hub-info-row"><div class="hub-info-row__badge"><span class="schedule-badge schedule-badge--' + it.cls + '">' + esc(it.label) + '</span></div>' +
          '<div class="hub-info-row__explain">' + (it.explain || '') + '</div></div>';
      });
      html += '</div>';
    }


    var count = bannerConfig.productCount || 0;
    if (count > 0) html += '<p class="hub-info-foot">Lots in this auction: <strong>' + count + '</strong></p>';
    html += '<p class="hub-info-foot hub-info-foot--muted">Every item is sold as-is, with all faults and defects, seen and unseen. The absence of a condition report does not imply the absence of defects.</p>';
    html += '<p class="hub-info-foot hub-info-foot--muted">Read the full <a href="/pages/bidder-terms">Bidder Terms &amp; Conditions</a>.</p>';

    contentEl.innerHTML = html;
  }

  // Expose open/close as globals for the onclick handlers in the Liquid.
  var _infoLastFocus = null;
  window.openHubInfoLightbox = function() {
    var lb = document.getElementById('hub-info-lightbox');
    if (!lb) return;
    _infoLastFocus = document.activeElement;
    lb.style.display = 'flex';
    document.body.classList.add('lightbox-open');
    var x = lb.querySelector('.hub-info-close');
    if (x) x.focus();
  };
  window.closeHubInfoLightbox = function() {
    var lb = document.getElementById('hub-info-lightbox');
    if (!lb || lb.style.display === 'none') return;
    lb.style.display = 'none';
    document.body.classList.remove('lightbox-open');
    if (_infoLastFocus && _infoLastFocus.focus) { try { _infoLastFocus.focus(); } catch (e) { /* gone */ } }
  };
  window.closeHubInfoLightboxOnOutsideClick = function(e) {
    if (e.target === document.getElementById('hub-info-lightbox')) {
      window.closeHubInfoLightbox();
    }
  };
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') window.closeHubInfoLightbox();
  });
})();
